import React, { useState, useEffect, useCallback } from 'react';
import MPDashboardLayout from '../../components/mp/MPDashboardLayout';
import { usersApi, exercisesApi } from '../../lib/mpApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Users, Loader2, Shield } from 'lucide-react';

// V14.10 — Unified MP roles aligned 1:1 with EMS roles. Legacy IDs
// (admin/author/user) are normalised to the unified set for display + edit.
const ROLES = ['super_admin', 'simcel', 'observer', 'participant'];
const ROLE_LABELS = {
  super_admin: 'Super Admin',
  simcel: 'Simcell',
  observer: 'Observer',
  participant: 'Participant',
};
const roleColors = {
  super_admin: 'text-red-600 bg-red-500/10',
  simcel: 'text-amber-600 bg-amber-500/10',
  observer: 'text-emerald-600 bg-emerald-500/10',
  participant: 'text-purple-600 bg-purple-500/10',
};
const normalizeRoles = (roles) => {
  const map = { admin: 'super_admin', author: 'simcel', viewer: 'participant', user: 'participant' };
  return (roles || []).map(r => map[r] || r);
};

const MPUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', roles: ['participant'], assigned_exercises: [],
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, exData] = await Promise.all([
        usersApi.getAll(),
        exercisesApi.getAll().catch(() => []),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setExercises(Array.isArray(exData) ? exData : []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', name: '', roles: ['participant'], assigned_exercises: [] });
    setShowDialog(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email, password: '', name: user.name,
      roles: normalizeRoles(user.roles).length ? normalizeRoles(user.roles) : ['participant'],
      assigned_exercises: user.assigned_exercises || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) { toast.error('Name and email are required'); return; }
    if (!editingUser && !formData.password) { toast.error('Password required for new user'); return; }
    setSaving(true);
    try {
      if (editingUser) {
        await usersApi.update(editingUser.user_id, { name: formData.name, roles: formData.roles, assigned_exercises: formData.assigned_exercises });
        if (formData.password) await usersApi.updatePassword(editingUser.user_id, formData.password);
        toast.success('User updated');
      } else {
        await usersApi.create(formData);
        toast.success('User created');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete "${user.name}"?`)) return;
    try { await usersApi.delete(user.user_id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  return (
    <MPDashboardLayout title="Users">
      <div className="space-y-4" data-testid="mp-users-page">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="mp-users-search" />
          </div>
          <Button onClick={openCreate} className="rounded-sm" data-testid="mp-create-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground"><Users className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No users found</p></div>
        ) : (
          <div className="space-y-2">
            {filtered.map(user => (
              <div key={user.user_id} className="flex items-center gap-4 p-4 border border-border rounded-sm" data-testid={`mp-user-${user.user_id}`}>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{user.name}</p>
                    <Badge variant={user.status === 'active' ? 'outline' : 'destructive'} className="rounded-sm text-[10px]">{user.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <div className="flex gap-1 mt-1">
                    {normalizeRoles(user.roles).map(r => (
                      <Badge key={r} className={`rounded-sm text-[10px] ${roleColors[r] || roleColors.participant}`}>{ROLE_LABELS[r] || r}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(user)} className="rounded-sm"><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(user)} className="rounded-sm text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Name</Label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Full name" className="rounded-sm" data-testid="mp-user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="user@example.com" className="rounded-sm" disabled={!!editingUser} data-testid="mp-user-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">{editingUser ? 'New Password (optional)' : 'Password'}</Label>
              <Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Password" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">Roles</Label>
              <div className="flex gap-2 flex-wrap">
                {ROLES.map(role => (
                  <button key={role} onClick={() => toggleRole(role)} className={`px-3 py-1.5 border rounded-sm text-sm transition ${
                    formData.roles.includes(role) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-accent'
                  }`} data-testid={`mp-user-role-${role}`}>
                    <Shield className="w-3 h-3 inline mr-1" />{ROLE_LABELS[role] || role}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="mp-user-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MPDashboardLayout>
  );
};

export default MPUsersPage;
